"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { isMarketingPath } from "@/lib/i18n/marketingLocale";
import type { UiLocale } from "@/lib/i18n/languages";
import { useI18n } from "./I18nProvider";

type LocalizedTextNode = Text & {
  __novilotStaticSource?: string;
  __novilotStaticTranslated?: string;
};

type SourceTranslator = (source: string) => string | undefined;

const localizedAttributes = ["aria-label", "placeholder", "title", "alt"] as const;

function splitWhitespace(value: string) {
  const match = value.match(/^(\s*)([\s\S]*?)(\s*)$/u);
  return { prefix: match?.[1] ?? "", text: match?.[2] ?? value, suffix: match?.[3] ?? "" };
}

function isWriterContent(node: Node) {
  const element = node.parentElement;
  return Boolean(element?.closest("[contenteditable='true'], input, textarea, script, style, [data-i18n-skip], [data-private]"));
}

function localizeText(node: LocalizedTextNode, locale: UiLocale, translateSource: SourceTranslator) {
  if (isWriterContent(node)) return;

  const current = node.data;
  const storedSource = node.__novilotStaticSource;
  const storedTranslated = node.__novilotStaticTranslated;
  const storedValueIsCurrent =
    storedSource !== undefined &&
    (current === storedSource || current === storedTranslated);

  if (locale === "en") {
    if (storedValueIsCurrent && storedSource !== undefined && current !== storedSource) {
      node.data = storedSource;
    }
    return;
  }

  // React may reuse a text node for a different string during navigation. In
  // that case, discard the old marker instead of translating stale content.
  const source = storedValueIsCurrent && storedSource !== undefined ? storedSource : current;
  const { prefix, text, suffix } = splitWhitespace(source);
  const translated = translateSource(text);
  if (!translated) return;

  const nextValue = `${prefix}${translated}${suffix}`;
  node.__novilotStaticSource = source;
  node.__novilotStaticTranslated = nextValue;
  if (node.data !== nextValue) node.data = nextValue;
}

function localizeAttributes(root: ParentNode, locale: UiLocale, translateSource: SourceTranslator) {
  const elements: HTMLElement[] = [];
  if (root instanceof HTMLElement) elements.push(root);
  elements.push(...Array.from(root.querySelectorAll<HTMLElement>("*")));

  for (const element of elements) {
    if (element.closest("[contenteditable='true'], [data-i18n-skip], [data-private]")) continue;
    for (const name of localizedAttributes) {
      const storageName = `data-novilot-static-${name}`;
      const savedSource = element.getAttribute(storageName);
      const current = element.getAttribute(name);
      const translationName = `data-novilot-static-translated-${name}`;
      const savedTranslation = element.getAttribute(translationName);
      const storedValueIsCurrent =
        savedSource !== null &&
        (current === savedSource || current === savedTranslation);

      if (locale === "en") {
        if (savedSource !== null && storedValueIsCurrent && current !== savedSource) {
          element.setAttribute(name, savedSource);
        }
        continue;
      }
      const source = storedValueIsCurrent && savedSource !== null ? savedSource : current;
      if (!source) continue;
      const translated = translateSource(source);
      if (!translated) continue;
      if (element.getAttribute(storageName) !== source) element.setAttribute(storageName, source);
      element.setAttribute(translationName, translated);
      if (element.getAttribute(name) !== translated) element.setAttribute(name, translated);
    }
  }
}

function localize(root: ParentNode, locale: UiLocale, translateSource: SourceTranslator) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const textNodes: LocalizedTextNode[] = [];
  while (walker.nextNode()) textNodes.push(walker.currentNode as LocalizedTextNode);
  textNodes.forEach((node) => localizeText(node, locale, translateSource));
  localizeAttributes(root, locale, translateSource);
}

/** Localizes legacy static strings while source components are gradually
 * converted to typed catalog keys. Writer-created and editable content is
 * explicitly excluded. The mutator only runs after hydration completes, so it
 * can never rewrite a text node that React is still hydrating. */
export function StaticStringLocalizer() {
  const { locale, translateSource } = useI18n();
  const previousLocale = useRef(locale);
  const pathname = usePathname();

  // Set only after the initial hydration commit, since a mount effect runs
  // once React has hydrated the server-rendered tree.
  const [isHydrated, setIsHydrated] = useState(false);
  useEffect(() => setIsHydrated(true), []);

  useEffect(() => {
    // Wait until React finishes hydrating. Rewriting a text node before React
    // hydrates the component that owns it makes the client render disagree with
    // the server HTML, which React reports as minified error #418.
    if (!isHydrated) return;
    // Public marketing pages use URL-based, server-rendered dictionaries. The
    // legacy DOM mutator must not change the language of an English URL.
    if (pathname && isMarketingPath(pathname)) return;
    const restoreEnglish = locale === "en" && previousLocale.current !== "en";
    previousLocale.current = locale;
    // English is authored directly. Do not walk/observe an entire manuscript
    // unless a previously translated interface needs restoring.
    if (locale === "en" && !restoreEnglish) return;

    localize(document.body, locale, translateSource);
    if (locale === "en") return;
    const observer = new MutationObserver((records) => {
      for (const record of records) {
        if (record.type === "characterData") {
          localizeText(record.target as LocalizedTextNode, locale, translateSource);
        } else {
          for (const node of record.addedNodes) {
            if (node instanceof Element || node instanceof DocumentFragment) localize(node, locale, translateSource);
            else if (node.nodeType === Node.TEXT_NODE) localizeText(node as LocalizedTextNode, locale, translateSource);
          }
        }
        if (record.type === "attributes") localizeAttributes(record.target as HTMLElement, locale, translateSource);
      }
    });
    observer.observe(document.body, {
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: [...localizedAttributes],
      subtree: true,
    });

    return () => observer.disconnect();
  }, [locale, pathname, translateSource, isHydrated]);

  return null;
}
